import React, { useMemo } from 'react';
import { ReactComponent as EmptyListIcon } from 'shared/img/icons/empty-list.svg';
import { isEmptyArray } from 'shared/utils/arrayUtils';
import Checkbox from '../../../andtComponents/Checkbox';
import ButtonMultiDropdown from '../../../andtComponents/ButtonMultiDropdown';
import icons from '../icons';
import styles from '../ChartButtonsMenu.module.scss';
import { useCurrencyContext } from 'app/contexts/Currency/CurrencyContext';
import Tooltip from 'shared/components/andtComponents/Tooltip';
import InfoPopover from '~/shared/components/andtComponents/InfoPopover/index.jsx';

const ViewMenu = ({
  trendLineHandler,
  onlyTrendRowHandler,
  trendRowHandler,
  wasteCostAllocationHandler,
  cumulativeHandler,
  goalsHandler,
  eventsHandler,
  disabled,
}) => {
  const currencyContext = useCurrencyContext();
  const goalOptions = useMemo(() => {
    if (currencyContext.currencyInUse?.code !== currencyContext.providerCurrency) {
      return [
        {
          id: 'showGoal',
          label: 'Show Goal',
          simpleOption: true,
          simpleOptionContent: (
            <Tooltip
              placement="top"
              title={
                <div className={styles.blockedGoalTooltip}>
                  {`Currency adjustments are not supported for goals, change the currency back to the provider's current currency to see it`}
                </div>
              }
            >
              <li data-disabled={true}>
                <Checkbox
                  isChecked={false}
                  isDisabled={true}
                  text="Show Goal"
                  className={styles.checkbox}
                  automationid="showGoal"
                />
              </li>
            </Tooltip>
          ),
        },
      ];
    }
    return [
      {
        id: 'showGoal',
        label: 'Show Goal',
        selected: !!goalsHandler?.value,
        content: (
          <div>
            {isEmptyArray(goalsHandler?.options) ? (
              <div className={styles.emptyBlock}>
                <EmptyListIcon />
                <p>No goals</p>
                <span>Set cost usage filters and save as new goal</span>
              </div>
            ) : (
              <>
                {goalsHandler?.options.map((goal) => (
                  <li
                    key={goal.uuid}
                    data-selected={goal.uuid === goalsHandler?.value}
                    onClick={() => goalsHandler?.onChange(goal.uuid === goalsHandler?.value ? null : goal.uuid)}
                    id={goal.uuid}
                  >
                    <span>{goal.title}</span>
                  </li>
                ))}
              </>
            )}
          </div>
        ),
      },
    ];
  }, [currencyContext.currencyInUse, currencyContext.providerCurrency, goalsHandler]);

  const options = [
    {
      id: 'showTrendRow',
      text: 'Show Trend Row',
      handler: trendRowHandler,
    },
    {
      id: 'showOnlyTrendRow',
      text: 'Show Only Trend Row',
      handler: onlyTrendRowHandler,
    },
    {
      id: 'showTrendline',
      text: 'Show Trendline',
      handler: trendLineHandler,
    },
    {
      id: 'showCumulative',
      text: 'Show Cumulative',
      handler: cumulativeHandler,
    },
    {
      id: 'allocateWasteCost',
      text: 'Allocate Waste Cost',
      handler: wasteCostAllocationHandler,
      tooltip:
        'Distribute the node-level unused cost among the pods of each node (with respect to the portion of the pod’s usage in the node).' +
        ' This ensures each cost is associated to an actual namespace (for accounting purposes).',
    },
    {
      id: 'showEvents',
      text: 'Show Events',
      handler: eventsHandler,
    },
  ]
    .filter((o) => !!o.handler)
    .map(({ text, handler, id, tooltip }, index) => ({
      id: id || index,
      simpleOption: true,
      simpleOptionContent: (
        <li data-disabled={disabled}>
          <Checkbox
            isChecked={handler.value}
            onChange={() => handler.onChange(!handler.value)}
            text={
              tooltip ? (
                <span>
                    <span className="pe-2">{text}</span>
                    <InfoPopover isSimple>{tooltip}</InfoPopover>
                  </span>
              ) : (
                text
              )
            }
            isDisabled={disabled}
            className={styles.checkbox}
            automationid={id}
          />
        </li>
      ),
    }));
  const menuOptions = useMemo(() => [...options, ...goalOptions], [options, goalOptions]);

  return (
    <ButtonMultiDropdown
      position="left"
      dropdownProps={{
        text: '',
        icon: icons.menu.optionsIcon,
        tooltipText: 'View Options',
      }}
      automationid="viewMenu"
      items={menuOptions}
    />
  );
};

export default ViewMenu;
